import { motion } from 'framer-motion'
import { useTranslations } from 'gt-react'
import {
  Blocks,
  GitBranch,
  Layers,
  Lock,
  type LucideIcon,
  Puzzle,
  Workflow,
} from 'lucide-react'
import { CountUp, SpotlightCard } from '@/components/reactbits'

const ease = [0.22, 1, 0.36, 1] as const

interface Feature {
  icon: LucideIcon
  titleKey: string
  descKey: string
  accent: string
}

const features: Feature[] = [
  { icon: Blocks, titleKey: 'items.modular.title', descKey: 'items.modular.description', accent: '#5865F2' },
  { icon: Lock, titleKey: 'items.secure.title', descKey: 'items.secure.description', accent: '#3BA55D' },
  { icon: Puzzle, titleKey: 'items.extensible.title', descKey: 'items.extensible.description', accent: '#EB459E' },
  { icon: Workflow, titleKey: 'items.workflows.title', descKey: 'items.workflows.description', accent: '#FAA81A' },
  { icon: GitBranch, titleKey: 'items.open.title', descKey: 'items.open.description', accent: '#5865F2' },
  { icon: Layers, titleKey: 'items.owned.title', descKey: 'items.owned.description', accent: '#EB459E' },
]

export function FeaturesGrid() {
  const t = useTranslations('features')

  return (
    <section className="relative py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease }}
          className="max-w-2xl"
        >
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t('heading')}
          </h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed sm:text-lg">
            {t('subheading')}
          </p>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5, delay: 0.1, ease }}
          className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4"
        >
          {[
            { value: 5, suffix: '+', label: 'Built-in modules' },
            { value: 100, suffix: '%', label: 'Your data ownership' },
            { value: 16, suffix: '', label: 'Field types supported' },
            { value: 99.9, suffix: '%', label: 'Uptime target', decimals: 1 },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl border border-border bg-card/50 px-5 py-4"
            >
              <div className="text-2xl font-bold tracking-tight tabular-nums sm:text-3xl">
                <CountUp
                  to={stat.value}
                  suffix={stat.suffix}
                  decimals={stat.decimals ?? 0}
                  duration={1.8}
                />
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Feature cards with SpotlightCard effect */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <motion.div
              key={feature.titleKey}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.05 * index, ease }}
            >
              <SpotlightCard
                spotlightColor={`${feature.accent}15`}
                className="h-full rounded-xl border border-border bg-card"
              >
                <div className="p-6">
                  <div
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${feature.accent}12`, color: feature.accent }}
                  >
                    <feature.icon className="h-[18px] w-[18px]" />
                  </div>
                  <h3 className="mt-4 text-sm font-semibold tracking-tight">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                    {t(feature.descKey)}
                  </p>
                </div>
              </SpotlightCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
